import { createContext, useContext, useState, type ReactNode, useCallback } from 'react';
import { Alert, Snackbar, type AlertColor } from '@mui/material';

export interface Notification {
    id: string;
    message: string;
    type: AlertColor;
    duration?: number;
    action?: {
        label: string;
        onClick: () => void;
    };
}

interface NotificationContextType {
    notifications: Notification[];
    addNotification: (notification: Omit<Notification, 'id'>) => string;
    removeNotification: (id: string) => void;
    clearAllNotifications: () => void;
    // Convenience methods
    showSuccess: (message: string, duration?: number) => string;
    showError: (message: string, duration?: number, action?: Notification['action']) => string;
    showWarning: (message: string, duration?: number) => string;
    showInfo: (message: string, duration?: number) => string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = (): NotificationContextType => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

interface NotificationProviderProps {
    children: ReactNode;
    maxNotifications?: number;
}

export const NotificationProvider = ({ children, maxNotifications = 5 }: NotificationProviderProps) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const generateId = useCallback(() => {
        return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }, []);

    const addNotification = useCallback(
        (notification: Omit<Notification, 'id'>): string => {
            const id = generateId();
            const newNotification: Notification = {
                ...notification,
                id,
                duration: notification.duration ?? (notification.type === 'error' ? 8000 : 4000),
            };

            setNotifications((prev) => {
                const updated = [newNotification, ...prev];
                // Limit the number of notifications
                return updated.slice(0, maxNotifications);
            });

            // Auto-remove notification after duration
            if (newNotification.duration && newNotification.duration > 0) {
                setTimeout(() => {
                    // eslint-disable-next-line react-hooks/immutability
                    removeNotification(id);
                }, newNotification.duration);
            }

            return id;
        },
        [generateId, maxNotifications]
    );

    const removeNotification = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((notification) => notification.id !== id));
    }, []);

    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
    }, []);

    // Convenience methods
    const showSuccess = useCallback(
        (message: string, duration?: number): string => {
            return addNotification({ message, type: 'success', duration });
        },
        [addNotification]
    );

    const showError = useCallback(
        (message: string, duration?: number, action?: Notification['action']): string => {
            return addNotification({ message, type: 'error', duration, action });
        },
        [addNotification]
    );

    const showWarning = useCallback(
        (message: string, duration?: number): string => {
            return addNotification({ message, type: 'warning', duration });
        },
        [addNotification]
    );

    const showInfo = useCallback(
        (message: string, duration?: number): string => {
            return addNotification({ message, type: 'info', duration });
        },
        [addNotification]
    );

    const contextValue: NotificationContextType = {
        notifications,
        addNotification,
        removeNotification,
        clearAllNotifications,
        showSuccess,
        showError,
        showWarning,
        showInfo,
    };

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
            <NotificationContainer notifications={notifications} onRemove={removeNotification} />
        </NotificationContext.Provider>
    );
};

interface NotificationContainerProps {
    notifications: Notification[];
    onRemove: (id: string) => void;
}

const NotificationContainer = ({ notifications, onRemove }: NotificationContainerProps) => {
    return (
        <>
            {notifications.map((notification, index) => (
                <Snackbar
                    key={notification.id}
                    open={true}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    style={{
                        top: `${80 + index * 70}px`, // Stack notifications vertically
                    }}
                    onClose={() => onRemove(notification.id)}
                >
                    <Alert
                        onClose={() => onRemove(notification.id)}
                        severity={notification.type}
                        variant="filled"
                        action={
                            notification.action ? (
                                <button
                                    onClick={() => {
                                        notification.action?.onClick();
                                        onRemove(notification.id);
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'inherit',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        marginLeft: '8px',
                                    }}
                                >
                                    {notification.action.label}
                                </button>
                            ) : undefined
                        }
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            ))}
        </>
    );
};
